import { snakeToTitleCase } from "./helpers"

export const DEFAULT_PAGE_SIZE = 10

export const PAYMENT_STATUS_OPTIONS = [
	{ value: "success", label: "Success" },
	{ value: "expired", label: "Expired" },
	{ value: "failed", label: "Failed" },
]

export const PAYMENT_METHOD_OPTIONS = [
	{ value: "truemoney_jumpapp", label: "TrueMoney JumpApp" },
	{ value: "promptpay", label: "PromptPay" },
	{ value: "card", label: "Card" },
	{ value: "mobile_banking_kbank", label: "Mobile Banking Kbank" },
	{ value: "mobile_banking_bbl", label: "Mobile Banking BBL" },
]

export const INITIAL_FILTERS = {
	paymentStatus: [],
	customerId: "",
	recurring: "",
	packageName: [],
	paymentMethod: [],
}

export const RECURRING_TRUE_VALUES = new Set(["yes", "true", "1"])
export const RECURRING_FALSE_VALUES = new Set(["no", "false", "0"])

export const getRecurringLabel = (value) => {
	if (value === undefined || value === null || value === "") {
		return "-"
	}
	const normalized = String(value).toLowerCase()
	if (RECURRING_TRUE_VALUES.has(normalized)) {
		return "Yes"
	}
	if (RECURRING_FALSE_VALUES.has(normalized)) {
		return "No"
	}
	return "-"
}

export const getPaymentStatusLabel = (value) => {
	if (value === undefined || value === null || value === "") {
		return "-"
	}
	const normalized = String(value).toLowerCase()
	return snakeToTitleCase(normalized)
}

export const getPaymentMethodLabel = (value) => {
	if (value === undefined || value === null || value === "") {
		return "-"
	}
	const normalized = String(value).trim()
	const option = PAYMENT_METHOD_OPTIONS.find((item) => item.value === normalized)
	if (option) {
		return option.label
	}
	return toSnakeTitleCase(value)
}

export const formatCsvCell = (value) => {
	const raw = value === undefined || value === null ? "" : String(value)
	if (raw === "") {
		return ""
	}
	if (/[",\n]/.test(raw)) {
		return `"${raw.replace(/"/g, '""')}"`
	}
	return raw
}

export const formatDateInput = (date) => {
	const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
	return local.toISOString().split("T")[0]
}

export const toSnakeTitleCase = (value) => {
	if (value === undefined || value === null || value === "") {
		return ""
	}
	return snakeToTitleCase(String(value).replace(/[-\s]+/g, "_"))
}

export const getDefaultDateRange = () => {
	const today = new Date()
	const dayOfMonth = today.getDate()

	let start
	let end

	if (dayOfMonth < 7) {
		const lastMonthDate = new Date(today.getFullYear(), today.getMonth(), 0)
		const firstDayPrevMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth(), 1)
		start = formatDateInput(firstDayPrevMonth)
		end = formatDateInput(lastMonthDate)
	} else {
		const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1)
		start = formatDateInput(firstDayCurrentMonth)
		end = formatDateInput(today)
	}

	return { start, end }
}

export const getNestedValue = (obj, path) => {
	return path.split(".").reduce((acc, key) => {
		if (acc === null || acc === undefined) return undefined
		return acc[key]
	}, obj)
}

export const getFirstNonEmptyValue = (obj, paths) => {
	for (const path of paths) {
		const value = getNestedValue(obj, path)
		if (value !== undefined && value !== null && value !== "") {
			return value
		}
	}
	return undefined
}

export const normalizeTransactionsResponse = (response, fallbackLimit) => {
	if (!response) {
		return {
			items: [],
			page: 1,
			limit: fallbackLimit,
			total: 0,
			totalPages: 1,
		}
	}

	const rawItems =
		(Array.isArray(response.transactions) && response.transactions) ||
		(Array.isArray(response.items) && response.items) ||
		(Array.isArray(response.data) && response.data) ||
		(Array.isArray(response.results) && response.results) ||
		(Array.isArray(response.records) && response.records) ||
		(Array.isArray(response) && response) ||
		[]

	const meta = response.meta || response.pagination || response.pageInfo || {}

	const derivedLimit =
		meta.limit || meta.perPage || response.limit || response.perPage || (response.pageSize && Number(response.pageSize)) || fallbackLimit

	const total = meta.total || response.total || response.totalCount || rawItems.length || 0
	const totalPages =
		meta.totalPages || meta.pages || response.totalPages || (response.total && derivedLimit ? Math.max(1, Math.ceil(total / derivedLimit)) : 1)

	const page =
		meta.page ||
		meta.currentPage ||
		response.page ||
		response.currentPage ||
		(meta.offset && derivedLimit ? Math.floor(meta.offset / derivedLimit) + 1 : 1)

	return {
		items: rawItems,
		page: Number(page) || 1,
		limit: Number(derivedLimit) || fallbackLimit,
		total: Number(total) || 0,
		totalPages: Number(totalPages) || 1,
	}
}

export const formatAmount = (value) => {
	if (value === undefined || value === null || value === "") return "-"
	if (typeof value === "number") {
		return `${value.toLocaleString()}`
	}
	const numberValue = Number(value)
	if (!Number.isNaN(numberValue)) {
		return `${numberValue.toLocaleString()}`
	}
	return value
}

export const normalizeMultiSelect = (value) => {
	if (!Array.isArray(value)) {
		return []
	}
	return value
		.map((item) => {
			if (item === undefined || item === null) return ""
			return String(item).trim()
		})
		.filter((item) => item !== "")
}

export const buildValueLabelMap = (options) => {
	const map = new Map()
	options.forEach((option) => {
		if (!option) return
		const key = String(option.value)
		const label = option.label ?? key
		map.set(key, label)
	})
	return map
}

export const summarizeSelectionLabels = (labels, maxVisible = 2) => {
	if (labels.length === 0) {
		return { visibleLabels: [], extraCount: 0 }
	}

	if (labels.length <= maxVisible) {
		return { visibleLabels: labels, extraCount: 0 }
	}

	return {
		visibleLabels: labels.slice(0, maxVisible),
		extraCount: labels.length - maxVisible,
	}
}
